import type {ReactNode, ChangeEvent, HTMLAttributes} from 'react';
import type React from 'react';
import type {CSSObject} from '@emotion/react';
import type {VariableSizeListProps} from 'react-window';

import type {InputProps} from 'Input';
import type {PropsWithChildren, QA, RequiredQA, getQAAttribute} from 'common';
import type {DropdownProps} from 'Dropdown';
import type {CheckboxTreeOption} from 'CheckboxTree';

/** Единичное значение - для SingleSelect */
export type SelectSingleValue = string | number;
/** Множественное значение - для Select */
export type SelectMultipleValue = SelectSingleValue[];

/** Option */
export type SelectOption<T = unknown> = {
  /** Label */
  label: string;
  /** Иконка
   *
   *
   * Наличие приведет к отключению virtualize
   */
  icon?: ReactNode;
  /** Дочерние option
   *
   * Отображаются только при multiple
   *
   * Наличие приведет к рендеру CheckboxTree
   * и отключению virtualize
   */
  childs?: SelectOption<T>[];
} & PropsWithChildren &
  Omit<CheckboxTreeOption, 'label'> &
  T;

/** Пропс компонента рендера option */
export type SelectItemProps<T = unknown> = {
  /** Option */
  option: SelectOption<T>;
  /** Выбран */
  checked: boolean; // isActive
} & Pick<SelectContextProps, 'onChange' | 'setIsOpen' | 'name' | 'value'> &
  Pick<HTMLAttributes<HTMLDivElement>, 'style'>;

/** Пропс компонента рендера всех option */
export type SelectItemsProps<T = unknown> = {
  /** Элементы Option */
  options: SelectOption<T>[];

  /** Ширина дропдауна в пикселях
   *
   * используется при virtualize
   */
  clientWidth: number | undefined;
} & RequiredQA &
  Pick<
    SelectContextProps,
    | 'state'
    | 'name'
    | 'value'
    | 'onChange'
    | 'multiple'
    | 'setIsOpen'
    | 'height'
    | 'maxHeight'
    | 'virtualize'
    | 'whiteSpace'
    | 'loadMore'
  > &
  Pick<SelectComponents, 'item'>;

/** Пропс компонента рендера контента dropdown */
export type SelectListProps = {
  /** Перегружаемые компоненты */
  components?: SelectComponents;
};

/** Обработчик изменения значения Select */
export type SelectListOnChange<T = unknown> = (
  /** Новое значение */
  value: SelectMultipleValue,
  /** Option на котором произошло событие  */
  option?: SelectOption<T>,
  /** Событие */
  event?: ChangeEvent<HTMLInputElement>
) => void;

/** Обработчик изменения значения SingleSelect */
export type SelectSingleOnChange<T = unknown> = (
  /** Новое значение */
  value: SelectSingleValue,
  /** Option на котором произошло событие  */
  option?: SelectOption<T>,
  /** Событие */
  event?: ChangeEvent<HTMLInputElement>
) => void;

/** Последний результат обработчика LoadMore или SelectFilter
 *
 * Можно расширять другими значениями, например сохраняя номер последней страницы
 */
export type LastResult<T = unknown> = {
  /** Новые или отфильтрованные option */
  options: SelectOption<T>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
};

/** Обработчик скроллинга в конец списка
 *
 * Позволяет загружать новые элементы при пагинации
 *
 * @returns Новые option
 */
export type LoadMore<S extends LastResult = LastResult, T = unknown> = (
  /** Текущие option */
  options: SelectOption<T>[],
  /** Последний результат вызова функции */
  lastResult?: S
) => Promise<S>;

/** Обработчик фильтрации option
 *
 * Используется для поиска значений
 * Пример containsFilter
 */
export type SelectFilter<S extends LastResult = LastResult, T = unknown> = {
  /** Обработчик
   *
   * @returns Отфильтрованные option. Несуществующие option будут добавлены
   */
  cb: (
    /** Введенный текст */
    query: string,

    /** Текущие option */
    options: SelectOption<T>[],
    /** Последний результат вызова функции */
    lastResult?: S
  ) => Promise<S>;

  /** debounce delay  */
  delay?: number;
};

/** Состояние Select */
export enum SelectState {
  /** По умолчанию */
  Default = 'default',
  /** Вызван обработчик filter */
  Searching = 'searching',
  /** Список отфильтрован */
  Filtred = 'filtred',
  /** Вызван обработчик loadMore */
  LoadingMore = 'loadingMore',
}

/** Перегружаемые компоненты */
type SelectComponents = {
  /** Подпись в верхнем баннере */
  hint?: ReactNode;
  /** Шапка */
  header?: ReactNode;
  /** Подвал */
  footer?: ReactNode;

  /** При поиске */
  searching?: ReactNode;
  /** При отсутствии результатов поиска */
  notFound?: ReactNode;

  /** Дробдаун (с шапкой, элементами и пр.) */
  list?: React.FC<SelectListProps>;
  /** Элементы списка */
  items?: React.FC<SelectItemsProps>;
  /** Элемент списка
   *
   * Не используется при virtualize
   */
  item?: React.FC<SelectItemProps>;
  /** Инпут */
  input?: React.FC<Omit<InputProps, 'value' | 'onChange'>>;
};

type CommonProps<T extends LastResult = LastResult> = {
  /** Значения */
  value: SelectMultipleValue;
  /** Обработчик изменения значения */
  onChange: SelectListOnChange;

  /** Множественный выбор */
  multiple?: boolean;
  /** Количество Chips с label выбранных элементов
   *
   * Без передачи, будет показано просто кол-во выбранных
   */
  showChips?: number;

  /** Максимальная длинна label выбранного элемента.
   *
   * Показывает tooltip при превышении */
  maxChipLength?: number;
  /** Обработчик длинны label выбранного элемента*/
  maxChipLengthFunc?: (label: string) => string;

  /** Использовать перенос строк в input */
  wrap?: boolean;

  /** Фильтр элементов
   *
   * При передаче будет возможность ввода текста в input
   *
   * Обработчик может фильтровать текущие элементы, а также возвращать новые
   *
   * которые будут добавлены (пример: при поиска на backend)
   */
  filter?: SelectFilter<T>;

  /** Использовать виртуализацию
   *
   * Использовть при большом количестве элементов > 1000
   *
   * Виртуализация отключается при передаче options с childs
   *
   * Виртуализация отключается при количестве элементов < 20
   */
  virtualize?: Pick<VariableSizeListProps, 'itemSize'> | boolean;
} & Pick<InputProps, 'disabled' | 'readOnly' | 'name'> &
  Pick<CSSObject, 'whiteSpace'>;

export type SelectProps<S extends LastResult = LastResult, T = unknown> = {
  /** Options */
  options: SelectOption<T>[];

  /** Кастомные свойства Dropdown
   *
   * Используется в специфических ситуациях
   */
  dropdownProps?: Partial<DropdownProps>;

  /** При скроллинге в конец списка
   *
   * Позволяет загружать новые элементы при пагинации
   */
  onLoadMore?: LoadMore<S>;
} & QA &
  CommonProps<S> &
  SelectListProps &
  Omit<InputProps, 'value' | 'onChange'> &
  Partial<Pick<DropdownProps, 'isOpen' | 'setIsOpen'>>;

export type SingleSelectProps<T extends LastResult = LastResult> = {
  /** Значение */
  value: SelectSingleValue | undefined;
  /** Обработчик изменения значения */
  onChange: SelectSingleOnChange;
} & Omit<SelectProps<T>, 'value' | 'onChange' | 'multiple'>;

export type SelectContextProps = {
  options: SelectOption[];
  filtred: SelectOption[];

  state: SelectState;
  setState: (state: SelectState) => void;

  getQA: ReturnType<typeof getQAAttribute>;

  onClear: () => void;

  loadMore: (target: HTMLDivElement | undefined) => void;
  filterOptions: (query: string) => void;
} & CommonProps &
  Pick<DropdownProps, 'isOpen' | 'setIsOpen' | 'height'> &
  Required<Pick<DropdownProps, 'maxHeight'>>;
